import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FlightsRebookActions,
  FlightsRebookNavigationFlow,
  FlightsRebookSelectors,
  FlightsRebookSteps,
  FlightsRebookThunk,
} from "@b2bportal/core-flights-rebook";
import { type AppDispatch, CallState } from "@b2bportal/core-types";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { RebookBookingOverlayProps } from "./types";

export const useRebookBookingOverlay = ({
  onSuccess,
}: RebookBookingOverlayProps) => {
  const { t } = useI18nContext();
  const dispatch = useDispatch<AppDispatch>();
  const bookingState = useSelector(FlightsRebookSelectors.getBookingState);

  React.useEffect(() => {
    if (bookingState?.state === "pending") {
      dispatch(FlightsRebookThunk.performPriceQuote(bookingState.request));
    } else if (
      bookingState?.state === "price-quote" &&
      bookingState.resource.state === CallState.Success
    ) {
      const { token, contractId } = bookingState.resource.data;
      dispatch(
        FlightsRebookThunk.performRebookFulfill({
          token,
          contractId,
        })
      );
    } else if (
      bookingState?.state === "fulfill" &&
      bookingState.resource.state === CallState.Success
    ) {
      onSuccess(bookingState.resource.data);
    }
  }, [dispatch, bookingState, onSuccess]);

  const goToStep = FlightsRebookNavigationFlow.useGoToStep();
  const dismissError = React.useCallback(() => {
    dispatch(FlightsRebookActions.dismissRebookError());
    goToStep(FlightsRebookSteps.SUMMARY);
  }, [dispatch, goToStep]);

  const context = React.useMemo(() => {
    switch (bookingState?.state) {
      case undefined:
      case "pending":
        return { state: "uninitialized" } as const;

      case "price-quote":
      case "fulfill":
        if (bookingState?.resource.state === CallState.InProcess) {
          return {
            state: "loading",
            message:
              bookingState.state === "price-quote"
                ? t("core-flights.rebook.loading.price-quote")
                : t("core-flights.rebook.loading.fulfill"),
          } as const;
        } else if (bookingState?.resource.state === CallState.Failed) {
          return {
            state: "error",
            error: bookingState.resource.error,
            dismissError,
          } as const;
        }
        return { state: "uninitialized" } as const;

      default:
        ensureExhaustive(bookingState);
    }
  }, [bookingState, dismissError, t]);

  return React.useMemo(() => ({ context }), [context]);
};
